// Chart-id → icon SVG. All 24×24 stroke icons in the same heroicons-ish
// style as the rest of the chart-card buttons (expand, close, kebab).
//
// CyclerButton renders the icon for the *next* chart in the cycle —
// "show what you're about to see" — so the icons are picked to be
// recognizable at button size, not for the chart they correspond to.

import React from 'react';
import type { ChartId } from './types';

const stroke = {
  className: 'w-5 h-5',
  fill: 'none' as const,
  stroke: 'currentColor',
  viewBox: '0 0 24 24',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  strokeWidth: 2,
};

export const CHART_ICONS: Record<ChartId, React.ReactNode> = {
  line: (
    <svg {...stroke}>
      <path d="M3 17l5-5 4 4 9-9" />
    </svg>
  ),
  // Heroicons "chart-bar"-ish — three rising rects.
  stats: (
    <svg {...stroke}>
      <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  // Heroicons "calendar-days" — visually echoes a heatmap grid.
  activity: (
    <svg {...stroke}>
      <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  pie: (
    <svg {...stroke}>
      <path d="M11 3v9h9a9 9 0 11-9-9z" />
      <path d="M14 3a7 7 0 017 7h-7V3z" />
    </svg>
  ),
  // Vertical bars on a baseline — three columns of varying height.
  enum_bar: (
    <svg {...stroke}>
      <path d="M4 20V10M10 20V4M16 20v-8M22 20H2" />
    </svg>
  ),
  // Histogram — adjacent bars with no gap, taller in the middle.
  histogram: (
    <svg {...stroke}>
      <path d="M3 20V14M7 20V10M11 20V6M15 20V10M19 20V14M3 20h18" />
    </svg>
  ),
  // Lines-of-text: short / long / short — reads as "words of varying weight".
  label_cloud: (
    <svg {...stroke}>
      <path d="M5 7h8M5 12h14M5 17h10" />
    </svg>
  ),
  // Horizontal bars — three rows, descending lengths.
  top_words: (
    <svg {...stroke}>
      <path d="M4 6h16M4 12h11M4 18h7" />
    </svg>
  ),
  // Map sum donut: same donut as `pie` with a small Σ glyph nearby to
  // mark "sum" mode. Sigma is rendered as a path (no fill text needed),
  // matching the rest of the stroke-only icon family.
  map_sum_pie: (
    <svg {...stroke}>
      <path d="M11 3v9h9a9 9 0 11-9-9z" />
      <path d="M14 3a7 7 0 017 7h-7V3z" />
      <path d="M18 18h4M18 18l2 2M18 18l2-2M22 22h-4" />
    </svg>
  ),
  // Map count donut.
  map_count_pie: (
    <svg {...stroke}>
      <path d="M11 3v9h9a9 9 0 11-9-9z" />
      <path d="M14 3a7 7 0 017 7h-7V3z" />
    </svg>
  ),
  // Map count bar.
  map_count_bar: (
    <svg {...stroke}>
      <path d="M4 20V10M10 20V4M16 20v-8M22 20H2" />
    </svg>
  ),
  // Multi-line — three lines criss-crossing, one rising, one mostly
  // flat, one falling.
  map_lines: (
    <svg {...stroke}>
      <path d="M3 17l4-4 4 2 4-6 6 4" />
      <path d="M3 7l4 3 4-2 4 4 6-2" />
    </svg>
  ),
  // Bars rising over a tick-marked x-axis — reads as "avg y at each
  // ordered key bucket." The visible axis ticks plus the rising heights
  // distinguish it from `map_count_bar` (which has descending bars and
  // no ordering implied) and from the unitless `histogram`.
  map_key_bar: (
    <svg {...stroke}>
      <path d="M3 20h18" />
      <path d="M6 20v-1M10 20v-1M14 20v-1M18 20v-1" />
      <path d="M6 18v-4M10 18v-7M14 18v-5M18 18v-9" />
    </svg>
  ),
  // Calendar with a check — the days a yes/no feature was answered Yes.
  bool_calendar: (
    <svg {...stroke}>
      <path d="M8 7V3m8 4V3M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  // Stacked columns — Yes days under No days, one column per week.
  bool_weeks: (
    <svg {...stroke}>
      <path d="M3 20h18" />
      <path d="M6 17v-3M12 17v-6M18 17v-4" />
      <path d="M6 11V9M12 8V5M18 10V7" />
    </svg>
  ),
};

export const CHART_LABELS: Record<ChartId, string> = {
  line: 'Line',
  stats: 'Stats',
  activity: 'Activity',
  pie: 'Distribution',
  enum_bar: 'Bar chart',
  histogram: 'Histogram',
  label_cloud: 'Label cloud',
  top_words: 'Top words',
  map_sum_pie: 'Totals by key',
  map_count_pie: 'Pair count by key (donut)',
  map_count_bar: 'Pair count by key (bar)',
  map_lines: 'Per-key over time',
  map_key_bar: 'Avg value by key',
  bool_calendar: 'Calendar',
  bool_weeks: 'Weeks',
};
